import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatDateTime, formatIDR, formatNumber } from "@/lib/format";
import { Modal } from "@/components/ui-kit";
import { Link } from "react-router-dom";
import {
  Truck, FileText, Bell, TrendingUp, TrendingDown, Minus, CheckCircle2, Clock,
  Image as ImageIcon, FileDown, ShoppingBag, Package, ArrowUpRight
} from "lucide-react";
import { SkeletonHero, SkeletonKPI, SkeletonTable } from "@/components/ui-kit";

/**
 * Iter v34aw — ClientDashboard refactored to Bento-Grid.
 * Preserves ALL existing data, endpoints, testids, and functionality.
 */

const PRICE_FIELD_LABELS = {
  price_non_vat: "Harga Non PPN",
  price_ex_vat: "Harga Exc PPN",
  price_with_vat: "Harga + PPN",
  delivery_cost: "Biaya Kirim",
  unloading_fee: "Biaya Bongkar",
};

function NotifPriceChange({ detail }) {
  if (!detail || typeof detail !== "object") return null;
  return (
    <div className="mt-1 space-y-0.5">
      {Object.entries(detail).map(([field, ch]) => {
        const o = Number(ch.old) || 0;
        const n = Number(ch.new) || 0;
        const diff = Number(ch.diff ?? (n - o));
        const pct = ch.pct != null ? Number(ch.pct) : (o ? (diff / o * 100) : null);
        const dir = ch.direction || (diff > 0 ? "naik" : diff < 0 ? "turun" : "tetap");
        const tone = dir === "naik" ? "text-emerald-700" : dir === "turun" ? "text-red-700" : "text-stone-500";
        const Icon = dir === "naik" ? TrendingUp : dir === "turun" ? TrendingDown : Minus;
        return (
          <div key={field} className={`flex flex-wrap items-center gap-1.5 text-[11px] ${tone}`}>
            <Icon className="h-3 w-3" />
            <span className="font-semibold uppercase">{dir}</span>
            <span className="text-stone-700">{PRICE_FIELD_LABELS[field] || field}:</span>
            <span className="font-mono-num">{formatIDR(o)} → {formatIDR(n)}</span>
            {diff !== 0 && (
              <span className="font-mono-num">
                ({diff > 0 ? "+" : ""}{formatIDR(diff)}{pct != null ? ` · ${diff > 0 ? "+" : ""}${pct.toFixed(2)}%` : ""})
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function ClientDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [viewFile, setViewFile] = useState(null);

  useEffect(() => {
    api.get("/client/dashboard").then((r) => setData(r.data)).catch((e) => {
      setErr(e?.response?.data?.detail || "Gagal memuat data");
    });
  }, []);

  if (err) {
    return (
      <div className="min-h-full bg-[#fafaf5] p-6 md:p-8">
        <div className="max-w-[1400px] mx-auto rounded-2xl bg-white border border-stone-200/70 p-12 text-center text-stone-500">{err}</div>
      </div>
    );
  }
  if (!data) return (
    <div className="min-h-full bg-[#fafaf5]">
      <div className="max-w-[1400px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
        <SkeletonHero dark />
        <SkeletonKPI count={4} />
        <SkeletonTable rows={4} cols={5} />
      </div>
    </div>
  );

  const totalPO = data.po_summary.length;
  const activePO = data.po_summary.filter((p) => !p.fully_delivered).length;
  const notifCount = data.notifications.filter((n) => (n.category || "notification") === "notification").length;
  const warningCount = data.notifications.filter((n) => n.category === "warning").length;

  return (
    <div className="min-h-full bg-[#fafaf5]">
      <div className="max-w-[1400px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
        {/* Hero */}
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-8 rounded-3xl bg-gradient-to-br from-blue-700 via-blue-800 to-emerald-800 text-white p-8 md:p-10 relative overflow-hidden" data-testid="client-hero">
            <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-emerald-400/20 blur-3xl"></div>
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.3em] text-blue-200 font-semibold mb-3">Portal Klien · {data.client_name}</div>
              <h1 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight">
                Selamat datang, <span className="italic text-emerald-300">{user?.name || "Klien"}</span>.
              </h1>
              <p className="text-blue-100 mt-3 text-sm max-w-xl">Pantau progres PO, riwayat Surat Jalan, dan notifikasi terbaru dari tim kami.</p>
              <div className="flex flex-wrap items-center gap-2 mt-6">
                <Link to="/client/catalog" className="px-4 py-2 rounded-full bg-white text-blue-900 text-xs font-medium hover:bg-emerald-50 transition flex items-center gap-1.5" data-testid="client-hero-catalog">
                  <ShoppingBag className="w-3.5 h-3.5"/> Katalog Material <ArrowUpRight className="w-3.5 h-3.5"/>
                </Link>
                <Link to="/client/surat-jalan" className="px-4 py-2 rounded-full border border-white/30 text-white text-xs hover:bg-white/10 transition flex items-center gap-1.5">
                  Riwayat SJ
                </Link>
              </div>
            </div>
          </div>

          <div className="col-span-12 md:col-span-4 grid grid-cols-2 gap-4">
            <div className="rounded-2xl bg-white border border-stone-200/70 p-4">
              <FileText className="w-4 h-4 text-blue-700 mb-2"/>
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Total PO</div>
              <div className="font-display text-3xl mt-1 text-stone-900">{totalPO}</div>
              <div className="text-[10px] text-stone-500 mt-0.5">{activePO} masih berjalan</div>
            </div>
            <div className="rounded-2xl bg-white border border-stone-200/70 p-4">
              <Truck className="w-4 h-4 text-emerald-700 mb-2"/>
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Surat Jalan</div>
              <div className="font-display text-3xl mt-1 text-stone-900">{data.latest_sj.length}</div>
              <div className="text-[10px] text-stone-500 mt-0.5">Pengiriman terbaru</div>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-blue-50 to-white border border-blue-100 p-4">
              <Bell className="w-4 h-4 text-blue-700 mb-2"/>
              <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">Notifikasi</div>
              <div className="font-display text-3xl mt-1 text-stone-900">{notifCount}</div>
              <div className="text-[10px] text-stone-500 mt-0.5">Update terbaru</div>
            </div>
            <div className={`rounded-2xl border p-4 ${warningCount > 0 ? "bg-red-50/60 border-red-200" : "bg-white border-stone-200/70"}`}>
              <Bell className={`w-4 h-4 mb-2 ${warningCount > 0 ? "text-red-600" : "text-stone-500"}`}/>
              <div className={`text-[10px] uppercase tracking-wider font-semibold ${warningCount > 0 ? "text-red-700" : "text-stone-500"}`}>Peringatan</div>
              <div className={`font-display text-3xl mt-1 ${warningCount > 0 ? "text-red-800" : "text-stone-900"}`}>{warningCount}</div>
              <div className="text-[10px] text-stone-500 mt-0.5">Perlu perhatian</div>
            </div>
          </div>
        </div>

        {/* PO + SJ */}
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 lg:col-span-8 rounded-2xl bg-white border border-stone-200/70 overflow-hidden" data-testid="client-po-progress">
            <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-700" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold">Progres</p>
                  <h2 className="font-display text-lg font-semibold text-stone-900">PO Anda</h2>
                </div>
              </div>
              <span className="text-xs px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100">{totalPO} PO</span>
            </div>
            <div className="divide-y divide-stone-100 max-h-[480px] overflow-y-auto">
              {data.po_summary.length === 0 && (
                <p className="px-5 py-12 text-center text-sm text-stone-400">Belum ada PO atas nama {data.client_name}.</p>
              )}
              {data.po_summary.map((po) => (
                <div key={po.id} className="px-5 py-4 hover:bg-stone-50/60 transition" data-testid={`client-po-${po.po_number}`}>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-mono-num text-xs text-stone-500">{po.po_number}</p>
                        {po.fully_delivered && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">
                            <CheckCircle2 className="h-2.5 w-2.5" /> Selesai
                          </span>
                        )}
                        {(po.file_id || po.file_path) && (
                          <button
                            onClick={() => setViewFile({ kind: "po", file_id: po.file_id, file_name: po.file_name, ref_number: po.po_number })}
                            className="inline-flex items-center gap-1 text-[10px] text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100 hover:bg-blue-100 transition"
                            title="Lihat foto/scan PO"
                            data-testid={`client-po-photo-${po.po_number}`}
                          >
                            <ImageIcon className="h-2.5 w-2.5" /> Lihat Foto
                          </button>
                        )}
                      </div>
                      <p className="text-sm font-medium text-stone-900 truncate">{po.client_name}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-mono-num text-sm">{formatNumber(po.delivered_qty, 2)} / {formatNumber(po.total_qty, 2)}</p>
                      <p className="text-[10px] uppercase tracking-widest text-stone-500">{Math.min(100, Math.round(po.progress))}%</p>
                    </div>
                  </div>
                  <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${po.fully_delivered ? "bg-emerald-500" : "bg-gradient-to-r from-blue-500 to-emerald-400"}`} style={{ width: `${Math.min(100, po.progress)}%` }} />
                  </div>
                  {po.items && po.items.length > 0 && (
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] text-stone-600">
                      {po.items.map((it, i) => (
                        <div key={i} className="flex items-center justify-between gap-2">
                          <span className="truncate">• {it.material_name}</span>
                          <span className="font-mono-num text-stone-500">{formatNumber(it.quantity || 0, 2)} {it.unit}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="col-span-12 lg:col-span-4 rounded-2xl bg-white border border-stone-200/70 overflow-hidden" data-testid="client-latest-sj">
            <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Truck className="h-4 w-4 text-emerald-700" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold">Update</p>
                  <h2 className="font-display text-lg font-semibold text-stone-900">Surat Jalan</h2>
                </div>
              </div>
              <Link to="/client/surat-jalan" className="text-[10px] uppercase tracking-wider text-blue-700 hover:text-blue-900 hover:underline" data-testid="client-sj-see-all">Semua →</Link>
            </div>
            <div className="divide-y divide-stone-100 max-h-[480px] overflow-y-auto">
              {data.latest_sj.length === 0 && (
                <p className="px-5 py-12 text-center text-sm text-stone-400">Belum ada Surat Jalan.</p>
              )}
              {data.latest_sj.map((sj) => (
                <div key={sj.id} className="px-5 py-3 hover:bg-stone-50/60 transition" data-testid={`client-sj-${sj.sj_number}`}>
                  <div className="flex items-start gap-2">
                    <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0">
                      <Truck className="h-3.5 w-3.5"/>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-[11px] text-stone-500 font-mono-num">{sj.sj_number} · {sj.date}</p>
                        {(sj.file_id || sj.file_path) && (
                          <button
                            onClick={() => setViewFile({ kind: "sj", file_id: sj.file_id, file_name: sj.file_name, ref_number: sj.sj_number })}
                            className="inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100 hover:bg-emerald-100 transition"
                            title="Lihat foto/scan Surat Jalan"
                            data-testid={`client-sj-photo-${sj.sj_number}`}
                          >
                            <ImageIcon className="h-2.5 w-2.5" /> Foto
                          </button>
                        )}
                      </div>
                      <p className="text-sm font-medium text-stone-900 truncate">{sj.delivery_location || "—"}</p>
                      <p className="text-[11px] text-stone-500 truncate">
                        {sj.vehicle_no || "—"} · {sj.driver || "—"}
                      </p>
                      {(sj.items || []).length > 0 && (
                        <p className="text-[11px] text-stone-600 mt-1 truncate">
                          {sj.items.slice(0, 2).map((it) => `${it.material_name} (${formatNumber(it.quantity || 0, 1)} ${it.unit})`).join(", ")}
                          {sj.items.length > 2 ? ` +${sj.items.length - 2} item` : ""}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="rounded-2xl bg-white border border-stone-200/70 overflow-hidden" data-testid="client-notifications">
          <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-stone-700" />
              <h2 className="font-display text-lg font-semibold text-stone-900">Notifikasi & Peringatan</h2>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-xs px-3 py-1 rounded-full ${data.notifications.length > 0 ? "bg-amber-50 text-amber-700 border border-amber-100" : "bg-stone-100 text-stone-600"}`}>
                {data.notifications.length}
              </span>
              <Link to="/client/notifications" className="text-[10px] uppercase tracking-wider text-blue-700 hover:text-blue-900 hover:underline" data-testid="client-notif-see-all">Semua →</Link>
            </div>
          </div>
          <div className="divide-y divide-stone-100">
            {data.notifications.length === 0 && (
              <p className="px-5 py-12 text-center text-sm text-stone-400">Tidak ada notifikasi baru.</p>
            )}
            {data.notifications.map((a) => {
              const isWarning = a.category === "warning";
              return (
                <div key={a.id} className="px-5 py-3 flex items-start gap-3 hover:bg-stone-50/60 transition" data-testid={`client-notif-${a.id}`}>
                  <div className={`mt-1.5 h-2 w-2 rounded-full ${isWarning ? "bg-red-500" : "bg-blue-500"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-stone-900">{a.title}</p>
                      {isWarning ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-100 uppercase tracking-wider">Peringatan</span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 uppercase tracking-wider">Notifikasi</span>
                      )}
                    </div>
                    {a.type === "price_change" && <NotifPriceChange detail={a.detail} />}
                    <p className="text-[11px] text-stone-500 font-mono-num mt-1 flex items-center gap-1">
                      <Clock className="h-2.5 w-2.5" />{formatDateTime(a.ts)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <FileViewerModal target={viewFile} onClose={() => setViewFile(null)} />
      </div>
    </div>
  );
}

function FileViewerModal({ target, onClose }) {
  if (!target) return null;
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const url = target.file_id ? `${apiBase}/api/files/${target.file_id}` : null;
  const isImg = (target.file_name || "").toLowerCase().match(/\.(png|jpe?g|gif|webp|heic|heif)$/);
  const label = target.kind === "po" ? "Foto/Scan PO" : "Foto/Scan Surat Jalan";
  return (
    <Modal open={!!target} onClose={onClose} title={`${label} — ${target.ref_number || ""}`} size="lg">
      <div className="space-y-3" data-testid="client-file-viewer">
        {!url ? (
          <p className="text-center py-12 text-sm text-stone-400">File tidak tersedia.</p>
        ) : isImg ? (
          <img src={url} alt={target.ref_number || "preview"} className="max-h-[70vh] w-full object-contain bg-stone-50 border border-stone-200 rounded-xl" />
        ) : (
          <iframe src={url} title={target.ref_number || "preview"} className="w-full h-[70vh] border border-stone-200 rounded-xl" />
        )}
        {url && (
          <div className="flex items-center justify-between border-t border-stone-100 pt-3">
            <p className="text-[11px] text-stone-500 truncate">{target.file_name || "file"}</p>
            <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-wider text-blue-700 hover:underline flex items-center gap-1">
              <FileDown className="h-3 w-3" /> Buka di tab baru
            </a>
          </div>
        )}
      </div>
    </Modal>
  );
}
